import { z } from "zod";

import {
  defaultFalseSchema,
  defaultTrueSchema,
  defaultEmptyStringSchema,
  defaultZeroSchema,
} from "./primitives";
import { catFormDataSchema } from "./cat-form-data.schema";
import { userFormDataSchema } from "./user-form-data.schema";
import { deliveryDataSchema } from "./delivery-data.schema";
import { subscriptionDataSchema } from "./subscription-data.schema";

// --- DS View State ---

const directSalesCheckoutViewStateSchema = z.object({
  showManualEntryButton: defaultFalseSchema,
  showAddressSelect: defaultFalseSchema,
  showShippingAddressFields: defaultFalseSchema,
  hasClickedFindAddress: defaultFalseSchema,
  hasSelectedAddressFromLookup: defaultFalseSchema,
  showShippingAddressWarning: defaultFalseSchema,
  showBillingAddressFields: defaultFalseSchema,
  billingSameAsShipping: defaultTrueSchema,
  showProcessingModal: defaultFalseSchema,
  shouldTriggerPayment: defaultFalseSchema,
});

const directSalesCatDetailsViewStateSchema = z.object({
  showFussinessAndFitness: defaultFalseSchema,
  showAllergiesAndMedical: defaultFalseSchema,
});

const directSalesViewStateSchema = z.object({
  checkout: z.preprocess(
    (val) => val ?? {},
    directSalesCheckoutViewStateSchema,
  ),
  catDetails: z.preprocess(
    (val) => val ?? {},
    z.record(z.string(), directSalesCatDetailsViewStateSchema),
  ),
});

export type TDirectSalesCheckoutViewState = z.infer<typeof directSalesCheckoutViewStateSchema>;
export type TDirectSalesCatDetailsViewState = z.infer<typeof directSalesCatDetailsViewStateSchema>;
export type TDirectSalesViewState = z.infer<typeof directSalesViewStateSchema>;

// --- Target User ---

const targetUserEnum = z.enum([ "CUSTOMER", "BRAND_AMBASSADOR" ]);

// --- DS Store Schema ---

export const directSalesStoreSchema = z
  .object({
    isStoreInitialised: defaultFalseSchema,
    versionMismatchDetected: defaultFalseSchema,
    cats: z.record(z.string(), catFormDataSchema).default({}),
    activeCatId: defaultEmptyStringSchema,
    user: z.preprocess((val) => val ?? {}, userFormDataSchema),
    delivery: z.preprocess((val) => val ?? {}, deliveryDataSchema),
    subscription: z.preprocess((val) => val ?? {}, subscriptionDataSchema),
    viewState: z.preprocess((val) => val ?? {}, directSalesViewStateSchema),
    isLoading: defaultFalseSchema,
    sufQuizVersion: defaultZeroSchema,
    savedAt: z.string().optional(),
    cardReaderId: defaultEmptyStringSchema,
    targetUser: targetUserEnum.default("CUSTOMER"),
  })
  .strip();

export type TDirectSalesStoreData = z.infer<typeof directSalesStoreSchema>;

export type TDirectSalesStoreInput = z.input<typeof directSalesStoreSchema>;

export function parseDirectSalesPersistedSnapshot(raw: unknown) {
  return directSalesStoreSchema.safeParse(raw);
}
